import { getCookieValue, setCookieValue } from '@/utils/cookie/cookie'
import { normalizeSearchParams } from '@/utils/searchParams/normalizeSearchParams'
import { useCallback, useEffect, useState } from 'react'
import type { SearchStateEntries } from './useSearchState'

const PRESETS_COOKIE_KEY = 'search_presets'

class PresetEventEmitter {
  private listeners: { [event: string]: Array<(...args: any[]) => void> } = {}

  on(event: string, callback: (...args: any[]) => void) {
    if (!this.listeners[event]) {
      this.listeners[event] = []
    }
    this.listeners[event].push(callback)
  }

  off(event: string, callback: (...args: any[]) => void) {
    if (!this.listeners[event]) return
    this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback)
  }

  emit(event: string, data?: any) {
    if (!this.listeners[event]) return
    this.listeners[event].forEach((callback) => callback(data))
  }
}

const presetEvents = new PresetEventEmitter()

type Preset = {
  id: string
  name: string
  params: Record<string, string | string[]>
}

type PresetStorage = {
  [presetId: string]: Preset
}

function getPresetsStorage(request?: Request): PresetStorage {
  try {
    const cookieValue = getCookieValue(PRESETS_COOKIE_KEY, request)
    if (!cookieValue) return {}

    const parsed = JSON.parse(cookieValue) as PresetStorage

    const validatedPresets: PresetStorage = {}
    for (const [id, preset] of Object.entries(parsed)) {
      if (preset && typeof preset === 'object' && preset.id && preset.name && preset.params) {
        validatedPresets[id] = {
          ...preset,
        }
      }
    }

    return validatedPresets
  } catch (error) {
    console.warn('Failed to parse presets from cookie:', error)
    return {}
  }
}

function urlSearchParamsToEntries(searchParams: URLSearchParams): SearchStateEntries {
  const entries: SearchStateEntries = []

  const keys = new Set<string>()
  for (const [key] of searchParams.entries()) {
    keys.add(key)
  }

  for (const key of keys) {
    const values = searchParams.getAll(key)
    if (values.length === 1) {
      entries.push([key, values[0]])
    } else if (values.length > 1) {
      entries.push([key, values])
    }
  }

  return entries
}

export function usePresets() {
  const [presets, setPresets] = useState<Preset[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const refreshPresets = useCallback(() => {
    try {
      const storage = getPresetsStorage()
      setPresets(Object.values(storage))
    } catch (error) {
      console.error('Failed to load presets:', error)
      setPresets([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshPresets()

    const handlePresetChange = () => {
      refreshPresets()
    }

    presetEvents.on('presets-changed', handlePresetChange)

    return () => {
      presetEvents.off('presets-changed', handlePresetChange)
    }
  }, [refreshPresets])

  const savePreset = useCallback((name: string, searchParams: URLSearchParams): string | null => {
    try {
      const existingPresets = getPresetsStorage()
      const id = `${name.trim().toLowerCase()}-${Date.now()}`

      const params = normalizeSearchParams(searchParams)

      const newPreset: Preset = {
        id,
        name,
        params,
      }

      const updatedPresets = {
        ...existingPresets,
        [id]: newPreset,
      }

      setCookieValue(PRESETS_COOKIE_KEY, updatedPresets)
      presetEvents.emit('presets-changed')

      return id
    } catch (error) {
      console.error('Failed to save preset:', error)
      return null
    }
  }, [])

  const loadPreset = useCallback((id: string): SearchStateEntries | null => {
    try {
      const storage = getPresetsStorage()
      const preset = storage[id]

      if (!preset) return null

      const searchParams = new URLSearchParams()
      for (const [key, value] of Object.entries(preset.params)) {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, v))
        } else {
          searchParams.set(key, value)
        }
      }

      return urlSearchParamsToEntries(searchParams)
    } catch (error) {
      console.error('Failed to load preset:', error)
      return null
    }
  }, [])

  const deletePreset = useCallback((id: string): boolean => {
    try {
      const storage = getPresetsStorage()

      if (!storage[id]) return false

      const updatedPresets = { ...storage }
      delete updatedPresets[id]

      setCookieValue(PRESETS_COOKIE_KEY, updatedPresets)
      presetEvents.emit('presets-changed')

      return true
    } catch (error) {
      console.error('Failed to delete preset:', error)
      return false
    }
  }, [])

  const getPresetById = useCallback(
    (id: string): Preset | null => {
      return presets.find((preset) => preset.id === id) || null
    },
    [presets]
  )

  const getPresetsGroupedByName = useCallback((): Record<string, Preset[]> => {
    const grouped: Record<string, Preset[]> = {}

    for (const preset of presets) {
      if (!grouped[preset.name]) {
        grouped[preset.name] = []
      }
      grouped[preset.name].push(preset)
    }

    return grouped
  }, [presets])

  return {
    presets,
    isLoading,
    savePreset,
    loadPreset,
    deletePreset,
    getPresetById,
    getPresetsGroupedByName,
    refreshPresets,
  } as const
}
